import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { LocationInput } from "@/components/LocationInput";

export default function Welcome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [interests, setInterests] = useState([]);
  const [other, setOther] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    if (user) {
      setCity(user.city_name || "");
      setCountry(user.country || "");
      setInterests(user.interests || []);
      setOther(user.interests_other || "");
    }
  }, [user]);

  const toggle = (name) => setInterests((cur) => cur.includes(name) ? cur.filter((i) => i !== name) : [...cur, name]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/users/me/preferences", { city, country, interests, interests_other: other });
      toast.success("Welcome to the Ripple ✦");
      window.location.assign("/");
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  return (
    <div className="silk-hero min-h-[70vh] flex items-center justify-center px-5 py-16">
      <div className="w-full max-w-md silk-card rounded-lg p-8 sm:p-10" data-testid="welcome-page">
        <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">One last thing</p>
        <h1 className="font-display text-3xl text-foreground mb-2">Make it yours</h1>
        <p className="text-sm text-muted-foreground mb-8">Tell us where you are and what calls to you — we'll match you with the right practitioners and moments.</p>
        <div className="space-y-6">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Your location</Label>
            <div className="mt-2">
              <LocationInput idPrefix="welcome" city={city} country={country}
                onChange={(v) => { setCity(v.city); setCountry(v.country); }} />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Your interests</Label>
            <div className="grid grid-cols-1 gap-2.5 mt-3" data-testid="welcome-interests">
              {categories.map((c) => (
                <label key={c.id} className="flex items-center gap-2.5 text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                  <Checkbox checked={interests.includes(c.name)} onCheckedChange={() => toggle(c.name)} />
                  {c.name}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="w-other" className="text-xs uppercase tracking-wider text-muted-foreground">Other interests (optional)</Label>
            <Input id="w-other" data-testid="welcome-other-input" value={other} placeholder="Anything else you're drawn to…"
              onChange={(e) => setOther(e.target.value)} className="mt-2" />
          </div>
          <Button data-testid="welcome-save-btn" disabled={busy} onClick={save} className="rounded-full px-8 w-full">
            {busy ? "Saving…" : "Save & explore"}
          </Button>
        </div>
      </div>
    </div>
  );
}
